import Parse from "parse";

/**
 * Renew the login JWT instead of signing the user out when it expires.
 *
 * The refresh token is kept in localStorage beside the access token. It was an
 * httpOnly cookie, which is stronger against XSS, but the API and the UIs are on
 * different registrable domains: the cookie was third-party, so Safari dropped it
 * outright and rotation never engaged there at all.
 */

const JWT_AUTH_FAILURE_PATTERN = /authentication required|token expired|token revoked|invalid token/i;

/** The same refusals in the login page's vocabulary. */
const SIGN_OUT_REASONS: Record<string, string> = {
  expired: "idle",
  session_expired: "expired",
  token_reused: "reused",
};

const DEFAULT_SIGN_OUT_REASON = "expired";

/**
 * Where the reason waits for the login page to collect it.
 *
 * App renders the Router, so the logout handler sits above router context and cannot
 * navigate to carry this on a URL the way the customer portal does. One key, written
 * on the way out and cleared on the way in, keeps the behaviour without moving the
 * app's root around.
 */
const REASON_KEY = "signOutReason";

let signOutReason = DEFAULT_SIGN_OUT_REASON;
let sessionLost: (() => void) | null = null;

/** Storage is absent during server rendering, so every read goes through this. */
const stored = (key: string) => (typeof localStorage === "undefined" ? null : localStorage.getItem(key));

/** The bearer this app is currently sending, or null before login restores one. */
const storedJwt = () => stored("authJwt");

/** Rotation spends this and returns its successor, so it is replaced on every renewal. */
const storedRefresh = () => stored("refreshToken");

/** The interceptor lives outside React, so it hands the teardown back here. */
export function onSessionLost(handler: () => void) {
  sessionLost = handler;
}

export function endSession() {
  sessionLost?.();
}

/** Left for the login page to show once, then forget. */
export function recordSignOutReason(reason?: string): void {
  const value = reason ?? signOutReason;
  signOutReason = DEFAULT_SIGN_OUT_REASON;
  try {
    sessionStorage.setItem(REASON_KEY, value);
  } catch (error: any) {
    // A browser refusing storage costs a message, not a logout — but a login page
    // that silently stops explaining itself is hard to account for, so say why.
    console.warn("Could not record the sign-out reason:", error?.message);
  }
}

/** Consumed on read, so a later visit to the login page is not still explaining itself. */
export function takeSignOutReason(): string | null {
  try {
    const value = sessionStorage.getItem(REASON_KEY);
    sessionStorage.removeItem(REASON_KEY);
    return value;
  } catch (error: any) {
    console.warn("Could not read the sign-out reason:", error?.message);
    return null;
  }
}

/**
 * A withAuth rejection on a request that carried a bearer. Before one is installed these
 * are the expected first-load race, and renewing would not help.
 */
export const isRenewableAuthFailure = (error: any) =>
  !!storedJwt() && error?.code === Parse.Error.SCRIPT_FAILED && JWT_AUTH_FAILURE_PATTERN.test(error?.message || "");

/**
 * The Parse session itself is gone, which renewal cannot mend: rotation slides a live
 * session, it does not resurrect a dead one. Matched by nothing above, a 209 was simply
 * rethrown — leaving a user on a page where every call failed, with no sign-out and no
 * way out but a manual reload.
 *
 * Guarded on a stored session token so a 209 raised before anyone has logged in cannot
 * bounce a visitor off the login page.
 */
export const isLostSession = (error: any) => !!stored("sessionToken") && error?.code === Parse.Error.INVALID_SESSION_TOKEN;

/**
 * Trade the refresh token for a fresh pair. The session token comes too: it slides the
 * Parse session, and is what the endpoint falls back to when there is no refresh token
 * yet — a session restored from before this shipped, for instance.
 */
async function rotateBearer(): Promise<boolean> {
  const sessionToken = stored("sessionToken");
  const refreshToken = storedRefresh();

  try {
    const response = await fetch(`${process.env.REACT_APP_PARSE_SERVER_URL}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(sessionToken ? { "X-Parse-Session-Token": sessionToken } : {}),
      },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
    const body = await response.json().catch(() => ({}));

    if (!response.ok || !body?.token) {
      signOutReason = SIGN_OUT_REASONS[body?.reason] ?? DEFAULT_SIGN_OUT_REASON;
      console.warn("Token rotation refused:", body?.reason ?? response.status);
      return false;
    }

    localStorage.setItem("authJwt", body.token);
    // The one it replaced is revoked; keeping it would fail the next renewal as a reuse.
    if (body.refreshToken) localStorage.setItem("refreshToken", body.refreshToken);
    Parse.CoreManager.set("REQUEST_HEADERS", { Authorization: `Bearer ${body.token}` });
    return true;
  } catch (error: any) {
    console.error("Token rotation failed:", error?.message);
    return false;
  }
}

let renewal: Promise<boolean> | null = null;

/** Single-flight: rotation revokes what it spends, so concurrent renewals would race. */
export function renewAuthToken(): Promise<boolean> {
  if (!renewal) {
    renewal = rotateBearer().finally(() => {
      renewal = null;
    });
  }
  return renewal;
}
